import {
  CAPTURE_CAPABILITY,
  type ServerMessage,
} from 'ts-components/PiChat/protocol';
import type { PiChatStart } from 'ts-components/PiChat/usePiChat';

export type AiBlockPanelContext = {
  targetType: 'dashboard' | 'homepage' | 'chart_group';
  dashboardId: string;
  objectId?: string;
};

export interface ResolveConnectOptionsInput {
  url: string;
  context: AiBlockPanelContext;
  latest: { dashletId: string | null; conversationId: string | null };
  /** `forkFrom` applies only while the block has no conversation of its own. */
  initial: { dashletId?: string; conversationId?: string; forkFrom?: string };
}

export const resolveConnectOptions = ({
  url,
  context,
  latest,
  initial,
}: ResolveConnectOptionsInput): PiChatStart => {
  const start = {
    url,
    profile: 'block-builder',
    capabilities: [CAPTURE_CAPABILITY],
  } satisfies Partial<PiChatStart>;
  const baseContext = {
    targetType: context.targetType,
    dashboardId: context.dashboardId,
    ...(context.objectId ? { objectId: context.objectId } : {}),
  };
  const dashletId = latest.dashletId ?? initial.dashletId;
  if (!dashletId) {
    return {
      ...start,
      context: baseContext,
      ...(latest.conversationId
        ? { conversationId: latest.conversationId }
        : {}),
    };
  }
  const conversationId = latest.conversationId ?? initial.conversationId;
  return {
    ...start,
    context: { ...baseContext, dashletId },
    ...(conversationId
      ? { conversationId }
      : initial.forkFrom
        ? { forkFrom: initial.forkFrom }
        : {}),
  };
};

/**
 * Tracks what each block-panel start sends. The newest conversation id seen
 * (`session.ready` or `push.result`) survives reconnects, so once one arrives
 * every start resumes it and `forkFrom` is never sent again.
 *
 * A whole-dashboard copy keeps the original's conversation id, which only that
 * conversation's owner may continue. If the start that resumes the opened id is
 * refused 'forbidden' before any `session.ready`, it is retried once as a fork.
 */
export const createBlockStart = (initial: {
  dashletId?: string;
  conversationId?: string;
  forkFrom?: string;
}) => {
  let known: { dashletId: string | null; conversationId: string | null } = {
    dashletId: null,
    conversationId: null,
  };
  let everReady = false;
  let refusedFork: string | null = null;
  let sent: PiChatStart | null = null;

  const getStart = (url: string, context: AiBlockPanelContext): PiChatStart => {
    sent = resolveConnectOptions({
      url,
      context,
      latest: known,
      initial: refusedFork
        ? { dashletId: initial.dashletId, forkFrom: refusedFork }
        : initial,
    });
    return sent;
  };

  /** Feeds a frame; returns true when the caller should reconnect as a fork. */
  const handleMessage = (message: ServerMessage): boolean => {
    if (message.type === 'session.ready') {
      everReady = true;
      known = {
        dashletId: message.context.dashletId ?? known.dashletId,
        conversationId: message.conversationId,
      };
      return false;
    }
    if (message.type === 'push.result') {
      known = {
        dashletId: message.dashletId,
        conversationId: message.conversationId ?? known.conversationId,
      };
      return false;
    }
    if (
      everReady ||
      refusedFork !== null ||
      message.type !== 'error' ||
      message.code !== 'forbidden' ||
      message.clientMessageId !== undefined ||
      sent?.conversationId === undefined ||
      sent.conversationId !== initial.conversationId
    ) {
      return false;
    }
    refusedFork = sent.conversationId;
    return true;
  };

  return { getStart, handleMessage };
};
